import axios from 'axios';

export const API_BASE =
  process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

const ACCESS_KEY = 'flowtrack_access';
const REFRESH_KEY = 'flowtrack_refresh';

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set({ access, refresh }) {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = tokens.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/*
 * تازه‌سازی توکن با یک نمونه‌ی جدا انجام می‌شود تا اینترسپتور دوباره روی خودش
 * اجرا نشود و حلقه نسازد. درخواست‌های هم‌زمان روی یک promise مشترک صف می‌شوند.
 */
const plain = axios.create({ baseURL: API_BASE });
let refreshing = null;

function refreshAccessToken() {
  if (!refreshing) {
    refreshing = plain
      .post('/auth/token/refresh/', { refresh: tokens.refresh })
      .then(({ data }) => {
        tokens.set({ access: data.access, refresh: data.refresh });
        return data.access;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

let onSessionExpired = () => {};
export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isAuthCall = original?.url?.includes('/auth/token');

    if (error.response?.status === 401 && !original._retry && !isAuthCall && tokens.refresh) {
      original._retry = true;
      try {
        const access = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${access}`;
        return api(original);
      } catch (e) {
        tokens.clear();
        onSessionExpired();
      }
    }
    return Promise.reject(error);
  }
);

/** پیام خطای قابل‌نمایش از پاسخ سرور بیرون می‌کشد. */
export function errorMessage(error, fallback = 'خطایی رخ داد. دوباره تلاش کنید.') {
  const data = error?.response?.data;
  if (!data) return error?.message === 'Network Error' ? 'ارتباط با سرور برقرار نشد.' : fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;

  const first = Object.values(data)[0];
  if (Array.isArray(first)) return first[0];
  if (typeof first === 'string') return first;
  return fallback;
}

// ---------------------------------------------------------------- نقاط پایانی

export const auth = {
  login: (username, password) => api.post('/auth/token/', { username, password }),
  logout: () => api.post('/auth/logout/', { refresh: tokens.refresh }),
  me: () => api.get('/auth/me/'),
  changePassword: (payload) => api.post('/auth/change-password/', payload),
};

export const cases = {
  list: (params) => api.get('/cases/', { params }),
  get: (id) => api.get(`/cases/${id}/`),
  timeline: (id) => api.get(`/cases/${id}/timeline/`),
  create: (payload) => api.post('/cases/create/', payload),
  confirmScan: (id, payload = {}) => api.post(`/cases/${id}/confirm-scan/`, payload),
  claim: (id) => api.post(`/cases/${id}/claim/`),
  release: (id) => api.post(`/cases/${id}/release/`),
  complete: (id, payload) => api.post(`/cases/${id}/complete/`, payload),
  call: (id) => api.post(`/cases/${id}/call/`),

  // ---- گزارشِ داخل سامانه
  // پاسخ ۲۰۴ یعنی هنوز چیزی نوشته نشده، نه خطا.
  getReport: (id) => api.get(`/cases/${id}/report/`),
  saveReport: (id, payload) => api.put(`/cases/${id}/report/`, payload),
  deliver: (id) => api.post(`/cases/${id}/deliver/`),

  // ---- نسخه‌ی پزشک
  addPrescription: (id, file) => {
    const form = new FormData();
    form.append('image', file, file.name || 'prescription.jpg');
    return api.post(`/cases/${id}/prescriptions/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // اسلش پایانی لازم است: بدون آن Django با ۳۰۱ به نشانی اسلش‌دار می‌فرستد و
  // مرورگر در پی‌گیری آن ریدایرکت، DELETE را به GET تبدیل می‌کند.
  removePrescription: (id, prescriptionId) =>
    api.delete(`/cases/${id}/prescriptions/${prescriptionId}/`),

  // ---- مطالعه‌ی تصویری (دایکام)
  listStudies: (id) => api.get(`/cases/${id}/studies/`),

  uploadStudy: (id, blob, { kind = 'raw', filename = 'study.zip', onProgress } = {}) => {
    const form = new FormData();
    form.append('archive', blob, filename);
    form.append('kind', kind);
    return api.post(`/cases/${id}/studies/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // آپلود چند صد مگابایتی؛ مهلت پیش‌فرض کوتاه است.
      timeout: 0,
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(event.loaded, event.total);
      },
    });
  },

  /**
   * دانلود مطالعه.
   *
   * فایل را در جاوااسکریپت نمی‌گیریم: ۳۰۰ مگابایت در حافظه‌ی مرورگر بد
   * است و نوار پیشرفت و ادامه‌ی دانلود را هم از دست می‌دهیم. به‌جایش یک
   * بلیت دو دقیقه‌ای می‌گیریم و مرورگر خودش مثل هر دانلود عادی می‌رود.
   */
  downloadStudy: async (studyId) => {
    const { data } = await api.get(`/studies/${studyId}/ticket/`);
    window.location.assign(data.url);
  },

  // ---- یادداشت‌های پرونده
  addNote: (id, text) => api.post(`/cases/${id}/notes/`, { text }),
  removeNote: (id, noteId) => api.delete(`/cases/${id}/notes/${noteId}/`),

  // ---- فوریت
  setUrgent: (id, isUrgent) => api.post(`/cases/${id}/urgent/`, { is_urgent: isUrgent }),

  // ---- لینک اختصاصی بیمار و QR آن
  setPatientLink: (id, link) => api.post(`/cases/${id}/patient-link/`, { link }),

  // لینک مشاهده — نشانی عمومی که با پیامک برای پزشک یا بیمار می‌رود.
  // POST بدون `fresh` لینک زنده‌ی موجود را برمی‌گرداند و تازه نمی‌سازد.
  shareLinks: (id) => api.get(`/cases/${id}/share-links/`),
  makeShareLink: (id, audience, fresh = false) =>
    api.post(`/cases/${id}/share-links/`, { audience, fresh }),
  revokeShareLink: (id, linkId) =>
    api.post(`/cases/${id}/share-links/${linkId}/revoke/`),

  /*
   * QR با <img src> ساده نمی‌آید: این نشانی توکن می‌خواهد و مرورگر هدر
   * Authorization را روی بارگذاری تصویر نمی‌فرستد. پس مثل دانلود CSV،
   * خودمان می‌گیریم و به object URL تبدیل می‌کنیم.
   */
  patientQr: (id, format = 'svg') =>
    api.get(`/cases/${id}/qr/`, {
      // نام پارامتر `format` نیست: DRF آن را رزرو کرده و ۴۰۴ می‌دهد.
      params: format === 'png' ? { as: 'png' } : {},
      responseType: 'blob',
    }),

  // ---- مدیر
  edit: (id, payload) => api.patch(`/cases/${id}/edit/`, payload),
  archive: (id, reason = '') => api.post(`/cases/${id}/archive/`, { reason }),
  restore: (id) => api.post(`/cases/${id}/restore/`),

  // ---- بازبینی فنی
  approve: (id, comment = '') => api.post(`/cases/${id}/approve/`, { comment }),
  reviewNotes: (id) => api.get(`/cases/${id}/review-notes/`),

  /**
   * درخواست اصلاح. اگر فایل صوتی باشد multipart می‌فرستیم، وگرنه JSON ساده.
   */
  requestRevision: (id, { text = '', audio = null, audioSeconds = null } = {}) => {
    if (!audio) {
      return api.post(`/cases/${id}/request-revision/`, { text });
    }
    const form = new FormData();
    if (text) form.append('text', text);
    form.append('audio', audio, 'note.webm');
    if (audioSeconds != null) form.append('audio_seconds', Math.round(audioSeconds));
    return api.post(`/cases/${id}/request-revision/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ---- پیش‌بررسیِ صفحه (دو عکس + یادداشتِ اختیاری، برای گروهِ فنیِ بله)
  sendScreenCheck: (id, { photo1, photo2, text = '', audio = null, audioSeconds = null }) => {
    const form = new FormData();
    form.append('photo_1', photo1, photo1.name || 'screen-1.jpg');
    if (photo2) form.append('photo_2', photo2, photo2.name || 'screen-2.jpg');
    if (text) form.append('text', text);
    if (audio) form.append('audio', audio, 'note.webm');
    if (audioSeconds != null) form.append('audio_seconds', Math.round(audioSeconds));
    return api.post(`/cases/${id}/screen-check/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

/** نشانی کامل فایل صوتی یادداشت (مسیر نسبی از سرور می‌آید). */
export function audioSourceUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${window.location.origin}${path}`;
}

export const users = {
  list: (params) => api.get('/users/', { params }),
  create: (payload) => api.post('/users/', payload),
  update: (id, payload) => api.patch(`/users/${id}/`, payload),
  toggleActive: (id) => api.post(`/users/${id}/toggle-active/`),
  remove: (id) => api.delete(`/users/${id}/`),
};

export const commissions = {
  matrix: () => api.get('/commissions/matrix/'),
  upsert: (payload) => api.post('/commissions/upsert/', payload),
};

export const feePolicy = {
  get: () => api.get('/fee-policy/'),
  update: (payload) => api.put('/fee-policy/', payload),

  // GET فقط نشان می‌دهد چه عوض می‌شود؛ POST اعمال می‌کند.
  previewRecalculation: (params) => api.get('/fee-policy/recalculate/', { params }),
  recalculate: (payload = {}) => api.post('/fee-policy/recalculate/', payload),
};

export const notifications = {
  list: (params) => api.get('/notifications/', { params }),
  unreadCount: () => api.get('/notifications/unread-count/'),
  markRead: (id) => api.post(`/notifications/${id}/read/`),
  markAllRead: () => api.post('/notifications/read-all/'),
};

/** نسخه‌ی سبک مطالعه — همان چیزی که نمایشگر می‌خواند. */
export const studies = {
  manifest: (studyId) => api.get(`/studies/${studyId}/view/manifest.json`),

  /**
   * یک تصویر نسخه‌ی سبک را به‌صورت `Image` آماده می‌کند.
   *
   * مستقیم `<img src=...>` کار نمی‌کند: مرورگر هدر Authorization را روی
   * بارگذاری تصویر نمی‌فرستد و پاسخ ۴۰۱ می‌شود. پس با axios (که
   * اینترسپتور توکن دارد) می‌گیریم و از blob می‌سازیم.
   *
   * فراخوان باید `revoke()` را در پاک‌سازی صدا بزند تا حافظه آزاد شود.
   */
  renditionImage: async (studyId, filename, version) => {
    const { data } = await api.get(`/studies/${studyId}/view/${filename}`, {
      responseType: 'blob',
      // نسخه از مانیفست می‌آید. بدون آن، پس از بازسازی نسخه‌ی کهنه از کش
      // مرورگر می‌آمد و تصویر با مانیفست جور در نمی‌آمد.
      params: version ? { v: version } : undefined,
    });
    const url = URL.createObjectURL(data);
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    return { image, revoke: () => URL.revokeObjectURL(url) };
  },
};

/** مسیرهایی که عامل آپلود می‌پاید — تعریفشان دست مدیر است. */
export const watchFolders = {
  list: () => api.get('/watch-folders/'),
  create: (payload) => api.post('/watch-folders/', payload),
  update: (id, payload) => api.patch(`/watch-folders/${id}/`, payload),
  remove: (id) => api.delete(`/watch-folders/${id}/`),
};

export const stats = {
  get: (params) => api.get('/stats/', { params }),

  /** گزارش کارانه برای نمایش داخل سامانه (جمع کل، جمع هر ادیتور، ردیف‌ها). */
  feeReport: (params) => api.get('/stats/fee-report/data/', { params }),

  /**
   * دانلود CSV گزارش کارانه.
   *
   * با یک <a href> ساده نمی‌شود: این نشانی توکن می‌خواهد و مرورگر هدر
   * Authorization را روی پیمایش معمولی نمی‌فرستد. پس فایل را خودمان
   * می‌گیریم و از blob دانلود می‌کنیم.
   */
  downloadFeeReport: async (params) => {
    const { data, headers } = await api.get('/stats/fee-report/', {
      params,
      responseType: 'blob',
    });

    const disposition = headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = match ? match[1] : 'flowtrack-fees.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

/** دفتر امنیتی — فقط مدیر. خواندنی است و نقطه‌ی نوشتن ندارد. */
export const security = {
  list: (params) => api.get('/security-log/', { params }),
  summary: () => api.get('/security-log/summary/'),
};

export const messengers = {
  linkToken: (provider) => api.post('/messengers/link-token/', { provider }),
  unlink: (provider) => api.post('/messengers/unlink/', { provider }),
};

export const metadata = () => api.get('/metadata/');

export default api;
